import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Shield, Lock, Cookie, ArrowLeft, ScrollText } from 'lucide-react';

import termosMd from '../legal/termos';
import privacidadeMd from '../legal/privacidade';
import lgpdMd from '../legal/lgpd';
import cookiesMd from '../legal/cookies';

const DOCS = {
  '/termos-de-uso':            { slug: 'termos-de-uso',            title: 'Termos de Uso',           icon: ScrollText, content: termosMd },
  '/politica-de-privacidade':  { slug: 'politica-de-privacidade',  title: 'Política de Privacidade', icon: Lock,       content: privacidadeMd },
  '/lgpd':                     { slug: 'lgpd',                     title: 'Aviso LGPD',              icon: Shield,     content: lgpdMd },
  '/cookies':                  { slug: 'cookies',                  title: 'Política de Cookies',     icon: Cookie,     content: cookiesMd },
};

/** Renderiza Markdown de forma simples sem dependência extra. */
function renderMarkdown(md) {
  const lines = (md || '').split('\n');
  const out = [];
  let inList = false;
  let listItems = [];
  const flush = () => { if (inList) { out.push(<ul key={`u${out.length}`} className="list-disc pl-6 my-3 space-y-1 text-slate-700">{listItems.map((t, i) => <li key={i} dangerouslySetInnerHTML={{ __html: inlineMd(t) }} />)}</ul>); inList = false; listItems = []; } };
  function inlineMd(s) {
    return s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/`(.+?)`/g, '<code class="px-1 py-0.5 rounded bg-slate-100 text-blue-800 text-sm">$1</code>');
  }
  lines.forEach((line, i) => {
    if (line.startsWith('# ')) { flush(); out.push(<h1 key={i} className="text-3xl font-extrabold text-slate-900 mt-2 mb-4">{line.slice(2)}</h1>); return; }
    if (line.startsWith('## ')) { flush(); out.push(<h2 key={i} className="text-xl font-bold text-slate-900 mt-8 mb-3 pb-2 border-b border-slate-200">{line.slice(3)}</h2>); return; }
    if (line.startsWith('### ')) { flush(); out.push(<h3 key={i} className="text-base font-bold text-slate-800 mt-5 mb-2">{line.slice(4)}</h3>); return; }
    if (line.startsWith('- ')) { inList = true; listItems.push(line.slice(2)); return; }
    if (line.startsWith('> ')) { flush(); out.push(<blockquote key={i} className="border-l-4 border-blue-700 pl-3 my-3 text-slate-600 italic" dangerouslySetInnerHTML={{ __html: inlineMd(line.slice(2)) }} />); return; }
    if (line.startsWith('|')) { flush(); out.push(<div key={i} className="font-mono text-xs text-slate-600 whitespace-pre overflow-x-auto" dangerouslySetInnerHTML={{ __html: inlineMd(line) }} />); return; }
    if (line.trim() === '---') { flush(); out.push(<hr key={i} className="my-6 border-slate-200" />); return; }
    if (line.trim() === '') { flush(); return; }
    flush();
    out.push(<p key={i} className="text-slate-700 leading-relaxed my-2" dangerouslySetInnerHTML={{ __html: inlineMd(line) }} />);
  });
  flush();
  return out;
}

export default function LegalPage() {
  const location = useLocation();
  const doc = DOCS[location.pathname];

  if (!doc) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="legal-not-found">
        <div className="text-center">
          <p className="text-slate-700 mb-3">Documento não encontrado.</p>
          <Link to="/" className="text-blue-700 hover:underline">Voltar à página inicial</Link>
        </div>
      </div>
    );
  }

  const Icon = doc.icon;
  return (
    <div className="min-h-screen bg-slate-50" data-testid={`legal-page-${doc.slug}`}>
      <header className="bg-slate-900 border-b border-slate-800 text-white">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white/80 hover:text-white text-sm" data-testid="legal-back-link">
            <ArrowLeft className="w-4 h-4" /> BlueGov
          </Link>
          <div className="flex items-center gap-2 text-white/90">
            <Icon className="w-4 h-4" />
            <span className="text-sm font-semibold">{doc.title}</span>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10 bg-white shadow-sm my-6 rounded-xl">
        <article className="prose max-w-none" data-testid="legal-content">
          {renderMarkdown(doc.content)}
        </article>

        <div className="mt-10 pt-6 border-t border-slate-200 flex flex-wrap gap-3 text-sm">
          <Link to="/termos-de-uso"          className="text-blue-700 hover:underline">Termos de Uso</Link>
          <Link to="/politica-de-privacidade" className="text-blue-700 hover:underline">Política de Privacidade</Link>
          <Link to="/lgpd"                    className="text-blue-700 hover:underline">LGPD</Link>
          <Link to="/cookies"                 className="text-blue-700 hover:underline">Cookies</Link>
        </div>
      </main>
    </div>
  );
}
